import { Button } from '@/components/ui'
import { useOnErrorAuto, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useMutation } from '@apollo/client'
import { usePrompt, useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React from 'react'
import { CREATE_EVENT_MUTATION } from '../mutations'
import type {
  CreateEventMutation,
  CreateEventMutationVariables,
} from '../types.graphql'
import { eventFormToInput } from '../util'
import { EditEventPrompt } from './edit-event-prompt'
import type { EventFormValues } from './event-form-fields'
import { defaultEventFormValues } from './event-form-fields'

interface Props {
  threadId: string
  /** The backend only lets the thread author and administrators add one. */
  mayAdd: boolean
  /** Called once the thread has become an event, so the thread can refetch. */
  onCreated?(): void
}

/** Turns a plain forum thread into an event people can sign up for. */
export const AddEventButton: React.FC<Props> = ({
  threadId,
  mayAdd,
  onCreated,
}) => {
  const translations = useTranslate({
    makeEvent: 'events.make-event',
    makeEventHelp: 'events.make-event-help',
    error: 'events.create-error',
  })

  const addPrompt = usePrompt()
  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()
  const { hasPermissions } = usePermissions()

  const [createEvent, { loading }] = useMutation<
    CreateEventMutation,
    CreateEventMutationVariables
  >(CREATE_EVENT_MUTATION, {
    onCompleted: () => onCreated?.(),
    onError: onErrorAuto(translations.error),
  })

  if (!mayAdd || !hasPermissions(PERMISSIONS.events.add.event)) return null

  async function handleClick() {
    const { data: values } = await addPrompt<EventFormValues | null>(
      resolve => (
        <EditEventPrompt
          initialValues={defaultEventFormValues()}
          title={translations.makeEvent}
          description={translations.makeEventHelp}
          onSubmit={resolve}
        />
      )
    )
    if (!values) return

    const { data } = await createEvent({
      variables: { thread: threadId, ...eventFormToInput(values) },
    })

    if (!data?.createEvent.ok) addToast('error', translations.error)
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="self-start"
      disabled={loading}
      onClick={handleClick}
    >
      {translations.makeEvent}
    </Button>
  )
}
